import {loadExcelWorkbook} from 'common/helpers/excelWorkbook'
import {ChevronLeft, ChevronRight, FileQuestion} from 'lucide-react'
import React, {FC, useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {useIntl} from 'react-intl'

interface Props {
  /** The file to show; null keeps the dialog closed. */
  file: File | null
  /** A workbook shows only this sheet (by name) when it has one; every sheet otherwise. */
  sheet?: string
  onClose: () => void
}

/** Rows shown per page of a spreadsheet or CSV. */
const ROWS_PER_PAGE = 100

type PreviewKind = 'image' | 'pdf' | 'sheet' | 'csv' | 'text' | 'docx' | 'heic' | 'none'

const extensionOf = (name: string) => name.split('.').pop()?.toLowerCase() ?? ''

/** How a file is shown, by its extension. ods/odt have no reader in the browser. */
export const filePreviewKind = (file: Pick<File, 'name'>): PreviewKind => {
  const extension = extensionOf(file.name)
  if (['png', 'jpg', 'jpeg', 'webp', 'gif'].includes(extension)) return 'image'
  if (extension === 'pdf') return 'pdf'
  if (extension === 'xlsx') return 'sheet'
  if (extension === 'csv') return 'csv'
  if (extension === 'txt') return 'text'
  if (extension === 'docx') return 'docx'
  if (['heic', 'heif'].includes(extension)) return 'heic'
  return 'none'
}

/** True when the file can be previewed at all. */
export const canPreviewFile = (file: Pick<File, 'name'>) => filePreviewKind(file) !== 'none'

/**
 * The uploaded file a server answer names. The server may write the name with a path or in
 * another case, so names are compared as lower-cased basenames; with a single file there is
 * nothing to choose between.
 */
export const findUploadedFile = (
  files: readonly File[],
  names: ReadonlyArray<string | undefined>
): File | undefined => {
  const base = (name: string | undefined) =>
    (
      String(name ?? '')
        .split(/[\\/]/)
        .pop() ?? ''
    )
      .trim()
      .toLowerCase()
  const wanted = names.map(base).filter(Boolean)
  return (
    files.find((file) => wanted.includes(base(file.name))) ??
    (files.length === 1 ? files[0] : undefined)
  )
}

/** A cell as text; exceljs gives dates, rich text and formula results as objects. */
const cellText = (value: any): string => {
  if (value === null || value === undefined) return ''
  if (value instanceof Date) {
    return `${value.getUTCMonth() + 1}/${value.getUTCDate()}/${value.getUTCFullYear()}`
  }
  if (typeof value === 'object') {
    if (Array.isArray(value.richText)) {
      return value.richText.map((part: any) => part?.text ?? '').join('')
    }
    if ('result' in value) return cellText(value.result)
    if ('text' in value) return cellText(value.text)
    return ''
  }
  return String(value)
}

/** Every worksheet of an .xlsx, as rows of text. */
const readWorkbook = async (file: File) => {
  const workbook = await loadExcelWorkbook(await file.arrayBuffer())
  return workbook.worksheets.map((worksheet) => {
    const columns = worksheet.columnCount
    const rows: string[][] = []
    worksheet.eachRow({includeEmpty: true}, (row) => {
      const cells: string[] = []
      for (let col = 1; col <= columns; col++) cells.push(cellText(row.getCell(col).value))
      rows.push(cells)
    })
    while (rows.length && rows[rows.length - 1].every((cell) => !cell.trim())) rows.pop()
    return {name: worksheet.name, rows}
  })
}

/** CSV text into rows, honouring quoted fields ("a, b" and "" inside quotes). */
const parseCsv = (text: string): string[][] => {
  const rows: string[][] = []
  let row: string[] = []
  let cell = ''
  let quoted = false
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]
    if (quoted) {
      if (ch === '"' && text[i + 1] === '"') {
        cell += '"'
        i++
      } else if (ch === '"') {
        quoted = false
      } else {
        cell += ch
      }
    } else if (ch === '"') {
      quoted = true
    } else if (ch === ',') {
      row.push(cell)
      cell = ''
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++
      row.push(cell)
      rows.push(row)
      row = []
      cell = ''
    } else {
      cell += ch
    }
  }
  if (cell || row.length) {
    row.push(cell)
    rows.push(row)
  }
  return rows
}

type Loaded =
  | {kind: 'url'; url: string; as: 'image' | 'pdf'}
  | {kind: 'sheets'; sheets: Array<{name: string; rows: string[][]}>}
  | {kind: 'text'; text: string}
  | {kind: 'html'; html: string}
  | {kind: 'none'}

/**
 * A file shown inside the page, whatever its format: images and PDFs as they are, spreadsheets
 * and CSVs as a table (a tab per sheet, 100 rows a page), Word documents as HTML, iPhone
 * photos converted to JPEG. Nothing leaves the browser.
 *
 * A Word document's HTML is shown in a sandboxed frame with no scripts, so a link or anything
 * else the document carries cannot run in the portal.
 */
const FilePreviewModal: FC<Props> = ({file, sheet, onClose}) => {
  const intl = useIntl()
  const [loaded, setLoaded] = useState<Loaded | null>(null)
  const [failed, setFailed] = useState(false)
  const [sheetIndex, setSheetIndex] = useState(0)
  const [page, setPage] = useState(0)

  useEffect(() => {
    setLoaded(null)
    setFailed(false)
    setSheetIndex(0)
    setPage(0)
    if (!file) return
    let cancelled = false
    let objectUrl: string | null = null
    const show = (value: Loaded) => {
      if (!cancelled) setLoaded(value)
    }
    const load = async () => {
      const kind = filePreviewKind(file)
      if (kind === 'image' || kind === 'pdf') {
        objectUrl = URL.createObjectURL(file)
        show({kind: 'url', url: objectUrl, as: kind})
      } else if (kind === 'heic') {
        const {default: heic2any} = await import('heic2any')
        const converted = await heic2any({blob: file, toType: 'image/jpeg', quality: 0.85})
        objectUrl = URL.createObjectURL(Array.isArray(converted) ? converted[0] : converted)
        show({kind: 'url', url: objectUrl, as: 'image'})
      } else if (kind === 'sheet') {
        const sheets = await readWorkbook(file)
        // Previewed from one sheet's card: that sheet alone, no tabs for the others. A name the
        // workbook does not have shows every sheet. Two lists side by side on one sheet come
        // back as "Sheet1 › A–B" and "Sheet1 › C–D": both are `Sheet1` of the workbook.
        const wanted = String(sheet ?? '')
          .split(' › ')[0]
          .trim()
          .toLowerCase()
        const match = wanted
          ? sheets.find((s) => s.name.trim().toLowerCase() === wanted)
          : undefined
        show({kind: 'sheets', sheets: match ? [match] : sheets})
      } else if (kind === 'csv') {
        show({kind: 'sheets', sheets: [{name: file.name, rows: parseCsv(await file.text())}]})
      } else if (kind === 'text') {
        show({kind: 'text', text: await file.text()})
      } else if (kind === 'docx') {
        const mammoth = await import('mammoth')
        const result = await mammoth.convertToHtml({arrayBuffer: await file.arrayBuffer()})
        show({kind: 'html', html: result.value})
      } else {
        show({kind: 'none'})
      }
    }
    load().catch(() => {
      if (!cancelled) setFailed(true)
    })
    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [file, sheet])

  const renderSheets = (sheets: Array<{name: string; rows: string[][]}>) => {
    const sheet = sheets[sheetIndex] ?? sheets[0]
    const allRows = sheet?.rows ?? []
    // The first row is the sheet's header: shown on every page, above the data, never paged.
    const header = allRows[0] ?? []
    const rows = allRows.slice(1)
    const pages = Math.max(1, Math.ceil(rows.length / ROWS_PER_PAGE))
    const current = Math.min(page, pages - 1)
    const start = current * ROWS_PER_PAGE
    const shown = rows.slice(start, start + ROWS_PER_PAGE)
    const columns = allRows.reduce((max, row) => Math.max(max, row.length), 0)
    const headCell: React.CSSProperties = {
      position: 'sticky',
      top: 0,
      zIndex: 1,
      background: 'var(--bs-gray-200)',
      color: 'var(--bs-gray-900)',
      fontWeight: 700,
      whiteSpace: 'nowrap',
      borderRight: '1px solid var(--bs-gray-300)',
      borderBottom: '2px solid var(--bs-primary)',
      padding: '0.55rem 0.75rem',
    }
    /** A data cell's grid lines: right and bottom, the container draws the outer edge. */
    const bodyCell: React.CSSProperties = {
      whiteSpace: 'nowrap',
      padding: '0.45rem 0.75rem',
      borderRight: '1px solid var(--bs-gray-300)',
      borderBottom: '1px solid var(--bs-gray-300)',
    }
    return (
      <>
        {sheets.length > 1 && (
          <ul className='nav nav-tabs nav-line-tabs mb-3 fs-7'>
            {sheets.map((entry, index) => (
              <li key={`${entry.name}-${index}`} className='nav-item'>
                <button
                  type='button'
                  className={`nav-link btn btn-link px-3 py-2${index === sheetIndex ? ' active' : ''}`}
                  onClick={() => {
                    setSheetIndex(index)
                    setPage(0)
                  }}
                >
                  {entry.name}
                </button>
              </li>
            ))}
          </ul>
        )}
        {allRows.length === 0 ? (
          <p className='text-muted mb-0'>
            {intl.formatMessage({id: 'DATA_UPLOAD.PREVIEW.EMPTY_SHEET'})}
          </p>
        ) : (
          <>
            <div
              className='table-responsive'
              style={{
                maxHeight: '60vh',
                overflowY: 'auto',
                border: '1px solid var(--bs-gray-300)',
                borderRadius: 8,
              }}
            >
              <table
                className='table table-sm table-striped table-hover fs-7 mb-0 align-middle'
                // Separate borders keep the sticky header's lines while the body scrolls.
                style={{borderCollapse: 'separate', borderSpacing: 0}}
              >
                <thead>
                  <tr>
                    <th style={{...headCell, width: 52, textAlign: 'right'}}>#</th>
                    {Array.from({length: columns}, (_, col) => (
                      <th key={col} style={headCell}>
                        {header[col] ?? ''}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {shown.map((row, rowIndex) => (
                    <tr key={start + rowIndex}>
                      <td
                        className='text-end'
                        style={{
                          ...bodyCell,
                          width: 52,
                          color: 'var(--bs-gray-500)',
                          background: 'var(--bs-gray-100)',
                        }}
                      >
                        {start + rowIndex + 1}
                      </td>
                      {Array.from({length: columns}, (_, col) => (
                        <td key={col} style={bodyCell}>
                          {row[col] ?? ''}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className='d-flex flex-wrap align-items-center justify-content-between gap-2 mt-3'>
              <span className='text-muted fs-7'>
                {intl.formatMessage(
                  {id: 'DATA_UPLOAD.PREVIEW.ROWS_RANGE'},
                  {
                    from: rows.length > 0 ? start + 1 : 0,
                    to: start + shown.length,
                    total: rows.length,
                  }
                )}
              </span>
              {pages > 1 && (
                <div className='d-flex align-items-center gap-2'>
                  <button
                    type='button'
                    className='btn btn-sm btn-light'
                    disabled={current === 0}
                    onClick={() => setPage(current - 1)}
                    aria-label={intl.formatMessage({id: 'DATA_UPLOAD.PREVIEW.PREVIOUS'})}
                  >
                    <ChevronLeft size={16} aria-hidden />
                  </button>
                  <span className='fs-7'>
                    {intl.formatMessage(
                      {id: 'DATA_UPLOAD.PREVIEW.PAGE'},
                      {page: current + 1, pages}
                    )}
                  </span>
                  <button
                    type='button'
                    className='btn btn-sm btn-light'
                    disabled={current >= pages - 1}
                    onClick={() => setPage(current + 1)}
                    aria-label={intl.formatMessage({id: 'DATA_UPLOAD.PREVIEW.NEXT'})}
                  >
                    <ChevronRight size={16} aria-hidden />
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </>
    )
  }

  const renderBody = () => {
    if (failed) {
      return (
        <p className='text-danger mb-0'>{intl.formatMessage({id: 'DATA_UPLOAD.PREVIEW.FAILED'})}</p>
      )
    }
    if (!loaded) {
      return (
        <div className='d-flex align-items-center gap-2 text-muted'>
          <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true' />
          {intl.formatMessage({id: 'DATA_UPLOAD.PREVIEW.LOADING'})}
        </div>
      )
    }
    if (loaded.kind === 'url' && loaded.as === 'image') {
      return (
        <div className='text-center'>
          <img
            src={loaded.url}
            alt={file?.name ?? ''}
            style={{maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain'}}
          />
        </div>
      )
    }
    if (loaded.kind === 'url') {
      return (
        <iframe
          src={loaded.url}
          title={file?.name ?? ''}
          style={{width: '100%', height: '70vh', border: 0}}
        />
      )
    }
    if (loaded.kind === 'sheets') return renderSheets(loaded.sheets)
    if (loaded.kind === 'text') {
      return (
        <pre
          className='mb-0 fs-7'
          style={{maxHeight: '70vh', overflow: 'auto', whiteSpace: 'pre-wrap'}}
        >
          {loaded.text}
        </pre>
      )
    }
    if (loaded.kind === 'html') {
      return (
        <iframe
          // No scripts, no same-origin: the document is shown, never run.
          sandbox=''
          srcDoc={`<!doctype html><meta charset="utf-8"><style>body{font-family:system-ui,sans-serif;font-size:14px;line-height:1.5;padding:12px;color:#181c32}table{border-collapse:collapse}td,th{border:1px solid #ccc;padding:4px 8px}img{max-width:100%}</style>${loaded.html}`}
          title={file?.name ?? ''}
          style={{width: '100%', height: '70vh', border: 0, background: '#fff'}}
        />
      )
    }
    return (
      <div className='d-flex align-items-center gap-2 text-muted'>
        <FileQuestion size={18} aria-hidden />
        {intl.formatMessage({id: 'DATA_UPLOAD.PREVIEW.NOT_AVAILABLE'})}
      </div>
    )
  }

  return (
    <Modal show={Boolean(file)} onHide={onClose} centered size='xl'>
      <Modal.Header closeButton>
        <Modal.Title className='fs-4 text-truncate' title={file?.name}>
          {file?.name}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>{renderBody()}</Modal.Body>
    </Modal>
  )
}

export default FilePreviewModal
